import { identify, resetIdentity, signInFailureReason, signInProps, track } from "./analytics";
import { getEntitlements, setGuestLimits } from "@foreman/protocol";
// A build made with VITE_GUEST_LIMITS=1 enforces the guest limits again (early access has them off). Read before anything below uses them.
if (import.meta.env.VITE_GUEST_LIMITS === "1") setGuestLimits(true);
import { reportError } from "./telemetry";
/**
 * Where sign-in talks to. Both are fixed when the app is built, never read from a link: a link that could name its own
 * relay could send someone's Google sign-in to a server that isn't ours.
 */
/** The Google OAuth client ID (public by design; there is no client secret in this flow). */
export const GOOGLE_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "750403179652-l51ps64k2rsi1g13a16r9g727ll28sta.apps.googleusercontent.com";
/** The relay's https address, e.g. https://foreman-relay.example.workers.dev. Empty: sign-in is not set up on this build. */
export const RELAY_HTTP: string = (import.meta.env.VITE_RELAY_URL ?? "").replace(/\/+$/, "");

/** The sign-in code in the address (`/?link=ABCD-2345`), or null. */
export function linkCodeFromUrl(search: string): string | null {
  const raw = new URLSearchParams(search).get("link");
  const code = raw?.toUpperCase().replace(/[^A-Z0-9]/g, "") ?? "";
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : null;
}

export type LinkInfo = { status: "pending"; label: string } | { status: "linked" } | { status: "expired" } | { status: "unknown" };

export async function fetchLinkInfo(code: string, relay = RELAY_HTTP): Promise<LinkInfo> {
  const res = await fetch(`${relay}/auth/link/info?code=${encodeURIComponent(code)}`);
  const body = (await res.json().catch(() => ({}))) as { status?: string; label?: string };
  if (body.status === "pending") return { status: "pending", label: body.label ?? "a computer" };
  if (body.status === "linked" || body.status === "expired") return { status: body.status };
  return { status: "unknown" };
}

export async function completeLink(code: string, credential: string, relay = RELAY_HTTP): Promise<{ ok: true; email: string; name: string | null } | { ok: false; message: string }> {
  try {
    const res = await fetch(`${relay}/auth/google`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code, credential }) });
    const body = (await res.json().catch(() => ({}))) as { email?: string; name?: string | null; message?: string };
    if (res.ok && body.email) return { ok: true, email: body.email, name: body.name ?? null };
    return { ok: false, message: body.message ?? `Sign-in failed (${res.status}).` };
  } catch {
    return { ok: false, message: "Couldn't reach Brocoded. Check your connection and try again." };
  }
}

/** Google's sign-in script, loaded when the page needs it. */
export function loadGoogleScript(): Promise<void> {
  const g = (window as any).google;
  if (g?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Couldn't load Google sign-in."));
    document.head.appendChild(s);
  });
}

// ---- Who is signed in on this phone ------------------------------------------------------
// Signing in on the phone gives an opaque token (the relay keeps only its hash). We keep it here, show who it is, and hand
// it to each computer so the computer links itself to the account. Signing in is optional: see FREE_SESSION_LIMIT on the
// computer and FREE_COMPUTERS here.

export interface Signed {
  token: string;
  email: string;
  name: string | null;
  /** Pseudonymous id for analytics, from the relay (never the email or Google ID). */
  analyticsId?: string;
}
const KEY = "foreman.account";
const listeners = new Set<() => void>();
let current: Signed | null | undefined;

export function getSigned(): Signed | null {
  if (current !== undefined) return current;
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null");
    current = v && typeof v.token === "string" && typeof v.email === "string" ? { token: v.token, email: v.email, name: typeof v.name === "string" ? v.name : null, analyticsId: typeof v.analyticsId === "string" ? v.analyticsId : undefined } : null;
  } catch {
    current = null;
  }
  return current;
}
function setSigned(v: Signed | null): void {
  current = v;
  if (v?.analyticsId) identify(v.analyticsId);
  else if (!v) resetIdentity();
  try {
    if (v) localStorage.setItem(KEY, JSON.stringify(v));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: signed in for this visit only */
  }
  for (const l of [...listeners]) l();
}
export const subscribeSigned = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** Is sign-in available in this build at all? */
export const signInAvailable = () => RELAY_HTTP !== "";

/** Exchange a Google credential for a sign-in on this phone. */
export async function signInWithGoogle(credential: string, relay = RELAY_HTTP): Promise<{ ok: true } | { ok: false; message: string }> {
  track("sign_in_started", signInProps());
  const res = await signInRequest(credential, relay);
  if (res.ok) track("sign_in_succeeded", signInProps());
  else track("sign_in_failed", signInProps({ reason: signInFailureReason(res.message) }));
  return res;
}

async function signInRequest(credential: string, relay: string): Promise<{ ok: true } | { ok: false; message: string }> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 20_000); // a hung request must end in a message the person can retry, never a spinner that stays
  try {
    const res = await fetch(`${relay}/auth/google`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ credential }), signal: ctl.signal });
    const body = (await res.json().catch(() => ({}))) as { token?: string; email?: string; name?: string | null; analyticsId?: string; message?: string };
    if (res.ok && body.token && body.email) {
      setSigned({ token: body.token, email: body.email, name: body.name ?? null, analyticsId: body.analyticsId });
      return { ok: true };
    }
    return { ok: false, message: body.message ?? `Sign-in failed (${res.status}).` };
  } catch {
    return { ok: false, message: "Couldn't reach Brocoded. Check your connection and try again." };
  } finally {
    clearTimeout(timer);
  }
}

// Another tab or window of the app signed in or out: follow it, so no tab keeps acting on a sign-in that is gone.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    current = undefined;
    const now = getSigned();
    if (now?.analyticsId) identify(now.analyticsId);
    for (const l of [...listeners]) l();
  });
}

/** A one-time code that signs this phone's Home Screen app in as the same account (its storage is separate from this tab's). Null when not signed in or offline. */
export async function createHandoffCode(relay = RELAY_HTTP): Promise<string | null> {
  const s = getSigned();
  if (!s || !relay) return null;
  try {
    const res = await fetch(`${relay}/auth/handoff`, { method: "POST", headers: { authorization: `Bearer ${s.token}` } });
    const body = (await res.json().catch(() => ({}))) as { code?: string };
    return res.ok && typeof body.code === "string" ? body.code : null;
  } catch {
    return null;
  }
}

/** Spend a hand-off code: this place is signed in as the account that made it. */
export async function redeemHandoffCode(code: string, relay = RELAY_HTTP): Promise<boolean> {
  if (!relay || getSigned()) return false;
  try {
    const res = await fetch(`${relay}/auth/handoff/redeem`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code }) });
    const body = (await res.json().catch(() => ({}))) as { token?: string; email?: string; name?: string | null; analyticsId?: string };
    if (!res.ok || !body.token || !body.email) return false;
    setSigned({ token: body.token, email: body.email, name: body.name ?? null, analyticsId: body.analyticsId });
    track("signin_handoff_redeemed");
    return true;
  } catch {
    return false;
  }
}

const GOOGLE_STATE_KEY = "foreman.google.state";

/** Whether the relay says Google accepts the full-page sign-in (its redirect address is registered in the Google console). */
export async function googleRedirectReady(relay = RELAY_HTTP): Promise<boolean> {
  if (!relay) return false;
  try {
    const res = await fetch(`${relay}/auth/google/ready`);
    return res.ok && ((await res.json().catch(() => ({}))) as { ready?: boolean }).ready === true;
  } catch {
    return false;
  }
}

/**
 * The Home Screen app on an iPhone can't show Google's accounts in a popup, so Google's own page takes over the window, the person signs in
 * there, and Google sends the result to the relay, which sends them back here with a one-time code. `state` is a secret this app keeps, so
 * only a sign-in that this app started is accepted when it returns.
 */
export function startGoogleRedirect(relay = RELAY_HTTP): void {
  const state = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");
  try {
    localStorage.setItem(GOOGLE_STATE_KEY, state);
  } catch {
    return; // nowhere to keep it: the return could not be checked
  }
  track("sign_in_started", signInProps({ via: "redirect" }));
  const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  url.search = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: `${relay}/auth/google/redirect`,
    response_type: "id_token",
    response_mode: "form_post",
    scope: "openid email profile",
    nonce: state,
    state,
    prompt: "select_account",
  }).toString();
  location.assign(url.toString());
}

/** The state this app made for a sign-in that is out at Google, if any. */
export const pendingGoogleState = (): string | null => {
  try {
    return localStorage.getItem(GOOGLE_STATE_KEY);
  } catch {
    return null;
  }
};
export const clearGoogleState = (): void => {
  try {
    localStorage.removeItem(GOOGLE_STATE_KEY);
  } catch {
    /* nothing to clear */
  }
};

export async function signOut(relay = RELAY_HTTP): Promise<void> {
  const s = getSigned();
  setSigned(null);
  if (s) await fetch(`${relay}/auth/signout`, { method: "POST", headers: { authorization: `Bearer ${s.token}` } }).catch((e) => reportError("sign-out-request", e));
}

/** Delete the signed-in account at the relay, then sign out here. A failure leaves the phone signed in so nothing disagrees silently. */
export async function deleteAccount(relay = RELAY_HTTP): Promise<{ ok: true } | { ok: false; message: string }> {
  const s = getSigned();
  if (!s) return { ok: false, message: "Not signed in." };
  try {
    const res = await fetch(`${relay}/auth/me`, { method: "DELETE", headers: { authorization: `Bearer ${s.token}` } });
    if (res.ok || res.status === 401) {
      setSigned(null);
      return { ok: true };
    }
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    return { ok: false, message: body.message ?? `Couldn't delete the account (${res.status}).` };
  } catch {
    return { ok: false, message: "Couldn't reach Brocoded. Check your connection and try again." };
  }
}

export interface AccountInfo {
  account: { analyticsId?: string; email: string; name: string | null; createdAt: number; lastSeen: number | null; plan?: "free" | "pro" };
  computers: { id: string; label: string | null; linkedAt: number; lastSeen: number | null; platform?: "mac" | "windows" | "linux" | null }[];
  usage: { connectionsToday: number; dailyLimit: number };
  limits: { maxComputers: number };
}

/** The signed-in account as the relay's database has it. A token the relay no longer knows signs this phone out. */
export async function fetchAccount(relay = RELAY_HTTP): Promise<AccountInfo | null> {
  const s = getSigned();
  if (!s || !relay) return null;
  try {
    const res = await fetch(`${relay}/auth/me`, { headers: { authorization: `Bearer ${s.token}` } });
    if (res.status === 401 || res.status === 403) {
      setSigned(null);
      return null;
    }
    if (!res.ok) return null;
    const info = (await res.json()) as AccountInfo;
    // Phones signed in before analytics existed pick up their id here.
    if (info.account.analyticsId && info.account.analyticsId !== s.analyticsId) setSigned({ ...s, analyticsId: info.account.analyticsId });
    return info;
  } catch {
    return null; // offline: keep what we show
  }
}

/** "Mac", "Windows" or "Linux" for a computer the account shares; empty if the computer hasn't said (older versions). */
export const osLabel = (p?: string | null): string => (p === "mac" ? "Mac" : p === "windows" ? "Windows" : p === "linux" ? "Linux" : "");

/** A guest phone may use one computer and one session; a signed-in phone has no limit. */
export const guestMayAddComputer = (computers: number): boolean => !signInAvailable() || !!getSigned() || computers < FREE_COMPUTERS;

/** Without an account a phone may use one computer; a second needs signing in. */
export const FREE_COMPUTERS = getEntitlements(null).limits.maxComputers;

/**
 * Where "Pair a computer" goes: sign in first (like WhatsApp Web: you are signed in on the phone, then scan the code the
 * computer shows, and that computer is signed in too), then the scanner. A person who skips signing in is a guest.
 * A second computer needs an account (`required`).
 */
export function pairingStart(_computers: number): { name: "scan" } {
  return { name: "scan" }; // no sign-in step up front: a guest's second computer is asked to sign in at the confirm step
}

// ---- Guest ------------------------------------------------------------------------------
// "Continue as guest" gives this phone a random name, kept only on the phone (never sent anywhere). It labels the guest in the
// app; signing in later simply replaces it, and the computers and sessions carry over because they never belonged to it.
const GUEST_KEY = "foreman.guest";
let guest: string | undefined;
export function getGuestId(): string {
  if (guest) return guest;
  try {
    const v = localStorage.getItem(GUEST_KEY);
    if (v && /^guest-[0-9a-f]{6}$/.test(v)) return (guest = v);
  } catch {
    /* private mode: a name for this visit */
  }
  guest = "guest-" + [...crypto.getRandomValues(new Uint8Array(3))].map((b) => b.toString(16).padStart(2, "0")).join("");
  try {
    localStorage.setItem(GUEST_KEY, guest);
  } catch {
    /* fine */
  }
  return guest;
}

/** Sign out: here, at every signed-in computer, and forget the computers on this phone (each is a QR scan away). Nothing on the computers is deleted. */
export async function signOutEverywhere(hub: { forgetAll(token: string | null): Promise<void> }): Promise<void> {
  const s = getSigned();
  await hub.forgetAll(s?.token ?? null).catch((e) => reportError("forget-all", e));
  await signOut();
}
